import React, {
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CustomControl } from "./CustomControl";
import LayersIcon from "@mui/icons-material/Layers";
import {
  Box,
  IconButton,
  Tooltip,
  Popover,
  Typography,
  Button,
} from "@mui/material";
import { useMap } from "react-leaflet";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { getHrefRel, getHrefTarget } from "@/utils/externalLink";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MIN_TAP_TARGET } from "@/styles/responsive";

// 地図全体の最小ズームレベル制限値
export const MAP_MIN_ZOOM = 7;
// 地図全体の最大ズームレベル制限値
export const MAP_MAX_ZOOM = 22;

export interface LayerToggleAction {
  label: string;
  icon: ReactNode;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

interface LayerChoice {
  label: string;
  value: string;
}

/**
 * 個別のレイヤー定義・設定情報の型定義
 * 地図上に表示するレイヤー（ベースレイヤー・オーバーレイ共通）の設定を表す
 */

interface BaseLayerDescriptor {
  /** レイヤーの一意識別子（内部キーとして使用） */
  name: string;
  /** UI上の表示ラベル（省略時はnameを使用） */
  label?: string;
  /** 無効化状態（ズームレベルやデータ未取得時など） */
  disabled?: boolean;
  /** レイヤーON/OFFとは別に表示する設定ボタンなどの操作 */
  action?: LayerToggleAction;

  // -- ヘルプ（詳細ポップオーバー）で表示する内容 --
  /** レイヤーの詳細な説明文 */
  description?: string;
  /** レイヤーが表示される最小のズームレベル */
  minZoom: number;
  /** レイヤーが表示される最大のズームレベル */
  maxZoom: number;
  /** ズームレベルに関するUI用補足文（省略時はminZoom, maxZoomから自動生成） */
  zoomDescription?: string;
  /** 出典の組織名やデータ名（例: 「国土数値情報」） */
  attributionText?: string;
  /** 出典元のWebリンクURL */
  attributionUrl?: string;
  /** attributionTextのうちリンクにする文字列（省略時は全文をリンクにする） */
  attributionLinkText?: string;

  // -- ロック状態（契約が必要で利用不可）のアップセル表示 --
  /** 契約が必要でロック中か。true のとき詳細に利用案内のCTAを出し、ズーム/出典は出さない */
  locked?: boolean;
  /** ロック時のCTAリンクURL（例: お問い合わせフォーム。将来は有料プラン導線に差し替え可） */
  lockedActionUrl?: string;
  /** ロック時のCTAリンク文言（既定「ご利用のお問い合わせ」） */
  lockedActionText?: string;
}

/**
 * 通常のチェックボックス項目
 */
interface DefaultLayerDescriptor extends BaseLayerDescriptor {
  type?: undefined;
  years?: never;
  choices?: never;
}

/**
 * 年度選択UIを持つ項目（`years` 必須）
 */
interface YearLayerDescriptor extends BaseLayerDescriptor {
  type: "year";
  years: number[];
  choices?: never;
  maxWidth?: string | number;
  /**
   * 年度選択 UI の形
   * - `row`（既定）: 親チェックボックス＋ラベル直下に年度ラジオを横並び（折り返しあり）で常時表示
   * - `list`: フォルダと同じ開閉見出し＋年度チェックボックス（同時に1つ）の縦リスト。年度が多い場合に使う
   */
  yearsLayout?: "row" | "list";
}

/**
 * 選択肢（ラジオボタン）を持つ項目（`choices` 必須）
 */
interface ChoiceLayerDescriptor extends BaseLayerDescriptor {
  type: "choice";
  choices: LayerChoice[];
  years?: never;
}

export type LayerDescriptor =
  | DefaultLayerDescriptor
  | YearLayerDescriptor
  | ChoiceLayerDescriptor;

/**
 * レイヤーを折りたたみ可能な小見出しでまとめるフォルダ
 * セクション（OverlayGroup）内で項目数が多い場合に、開閉して表示領域を節約する。
 * フォルダ自体は ON/OFF を持たず、開閉状態は LayerGroupControl 内で保持する。
 */
export interface LayerFolder {
  type: "folder";
  /** フォルダの見出し（開閉状態のキーにも使うため、同一グループ内で一意にする） */
  title: string;
  /** フォルダに属する項目。フォルダを入れ子にすることもできる */
  items: OverlayItem[];
}

/** セクション内に並べられる項目（レイヤー、またはレイヤーをまとめたフォルダ） */
export type OverlayItem = LayerDescriptor | LayerFolder;

/**
 * オーバーレイレイヤーのグループ
 * 複数のレイヤーを論理的にまとめて表示するため
 */
export interface OverlayGroup {
  /** グループの見出し */
  title: string;
  /** グループに属する項目一覧（レイヤーまたはフォルダ） */
  layers: OverlayItem[];
}

/**
 * LayerGroupControlのProps
 * 地図レイヤーの選択UIに必要なデータと操作コールバックを受け取る
 */
interface LayerGroupControlProps {
  /** オーバーレイグループ（複数選択可能）の一覧 */
  overlayGroups: OverlayGroup[];
  /** 各オーバーレイの表示/非表示状態 */
  overlayVisibility: Record<string, boolean>;
  /** オーバーレイ切り替え時のコールバック */
  onToggleOverlay: (name: string, next: boolean) => void;
  /** choice型オーバーレイの現在値 */
  choiceValues?: Record<string, string>;
  /** choice型オーバーレイの選択変更時コールバック */
  onSelectChoice?: (name: string, value: string) => void;
  /** ここに含まれるグループは、表示は残したまま操作不可にする */
  lockedGroupTitles?: string[];
  /** レイヤー名 → 項目名の横に出す短い補足（例: 「データなし」）。年度リスト項目は見出し行に出す */
  layerBadges?: Record<string, string>;
  /**
   * yearsLayout: "list" の項目について、レイヤー名 → 年度 → 地図中心付近に写真があるか。
   * 分かっている年度だけ印を付け、ない年度は薄く表示する
   */
  yearAvailability?: Record<string, Record<number, boolean>>;
  /** yearsLayout: "list" の年度リストを開閉した時に呼ぶ（開いている間だけ有無を調べる用途） */
  onYearListToggle?: (name: string, expanded: boolean) => void;
}

/**
 * 地図レイヤー選択コントロール
 *
 * 地図右上に表示されるレイヤー切り替えUI。
 * - オーバーレイ: チェックボックスで複数選択可能
 *
 * ホバー/クリックでパネルが展開される。
 */

/**
 * レイヤーのズームレベル制限に関する説明テキストを生成します。
 * `zoomDescription` が設定されている場合はそれを最優先で使用し、
 * 未設定の場合は `minZoom` と `maxZoom` の値に基づいて自動生成します。
 *
 * @param layer 対象のレイヤー情報
 * @returns 画面表示用のズームレベル説明文字列
 */
export const getZoomLevelDescription = (layer: LayerDescriptor): string => {
  if (layer.zoomDescription) {
    return layer.zoomDescription;
  }
  const min = layer.minZoom ?? MAP_MIN_ZOOM;
  const max = layer.maxZoom ?? MAP_MAX_ZOOM;

  if (min === MAP_MIN_ZOOM && max === MAP_MAX_ZOOM) {
    return "すべてのズームレベルで表示";
  }
  if (min === max) {
    return `ズームレベル ${min}`;
  }
  if (max === MAP_MAX_ZOOM) {
    return `ズームレベル ${min} 以上`;
  }
  return `ズームレベル ${min} 〜 ${max}`;
};

const renderAttributionText = (layer: LayerDescriptor): ReactNode => {
  const { attributionText, attributionUrl, attributionLinkText } = layer;
  if (!attributionText) return null;
  if (!attributionUrl) return <span>{attributionText}</span>;

  const linkText = attributionLinkText ?? attributionText;
  const linkStartIndex = attributionText.indexOf(linkText);
  if (linkStartIndex === -1) return <span>{attributionText}</span>;

  return (
    <>
      {attributionText.slice(0, linkStartIndex)}
      <a
        href={attributionUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#3b82f6", textDecoration: "underline" }}
      >
        {linkText}
      </a>
      {attributionText.slice(linkStartIndex + linkText.length)}
    </>
  );
};

/**
 * フォルダや年度リストの開閉に使う見出しボタン
 * チェックボックスの位置に開閉アイコンを置き、項目名をクリックしても開閉できるようにする
 */
const DisclosureHeader: React.FC<{
  title: string;
  expanded: boolean;
  onToggle: () => void;
}> = ({ title, expanded, onToggle }) => (
  <button
    type="button"
    aria-expanded={expanded}
    onClick={(event) => {
      event.preventDefault();
      event.stopPropagation();
      onToggle();
    }}
    style={{
      display: "flex",
      alignItems: "center",
      gap: "2px",
      flex: 1,
      minWidth: 0,
      padding: "2px 0",
      border: "none",
      background: "none",
      font: "inherit",
      color: "inherit",
      cursor: "pointer",
      textAlign: "left",
    }}
  >
    {expanded ? (
      <ExpandMoreIcon style={{ fontSize: "18px", color: "#6b7280" }} />
    ) : (
      <ChevronRightIcon style={{ fontSize: "18px", color: "#6b7280" }} />
    )}
    <span style={{ fontWeight: 500 }}>{title}</span>
  </button>
);

/**
 * 年度リストが実際に画面へ出ている間だけ、親へ「開いている」を通知する。
 *
 * 見出しのクリックだけで通知すると、親フォルダやレイヤー選択パネルが閉じて
 * リストがアンマウントされた時に「閉じた」が届かず、親側の有無確認
 * （useNendoPhotoYearAvailability）が地図移動のたびに外部へリクエストし続けてしまう。
 * 表示条件そのものにマウントが連動する子要素として置くことで、
 * 見出し・フォルダ・パネルのどれで閉じても必ず false が届くようにする。
 */
const YearListVisibilityReporter: React.FC<{
  name: string;
  onChange?: (name: string, expanded: boolean) => void;
}> = ({ name, onChange }) => {
  // onChange の参照が変わっても通知をやり直さないよう ref 経由で呼ぶ
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onChangeRef.current?.(name, true);
    return () => {
      onChangeRef.current?.(name, false);
    };
  }, [name]);

  return null;
};

/** レイヤー選択パネルの最小幅（左右の余白を除く中身の幅） */
const LAYER_PANEL_MIN_WIDTH = 290;
/** レイヤー選択パネルの左右の余白 */
const LAYER_PANEL_PADDING_X = 16;
/** SP でレイヤー選択パネルと地図の枠の間に空ける余白（Leaflet のコントロールの余白に揃える） */
const LAYER_PANEL_MAP_MARGIN = 10;
/**
 * SP で右下のコントロール（凡例など）を避けた結果、パネルの高さがこれより低くなる場合は
 * 避けずに地図の下端まで使う（凡例を開いている時などにパネルが潰れないように）
 */
const LAYER_PANEL_MIN_HEIGHT_AVOIDING_BOTTOM_CONTROLS = 200;

/**
 * SP でレイヤー選択パネルを地図の枠の中に収めるための最大の高さ・幅を求める。
 *
 * パネルは地図右上のコントロール領域に置かれ、地図の枠の外へは広がれない
 * （はみ出た部分を押すと下の地図が反応する）。右下のコントロール（凡例など）は
 * パネルより手前に描かれるため、高さはその上端までに収める。
 */
const calcMobileLayerPanelBounds = (
  mapContainer: HTMLElement,
  panel: HTMLElement
): { maxHeight: number; maxWidth: number } => {
  const mapRect = mapContainer.getBoundingClientRect();
  const panelRect = panel.getBoundingClientRect();
  const bottomRightRect = mapContainer
    .querySelector(".leaflet-bottom.leaflet-right")
    ?.getBoundingClientRect();
  const heightAboveBottomControls =
    bottomRightRect && bottomRightRect.height > 0
      ? bottomRightRect.top - panelRect.top - LAYER_PANEL_MAP_MARGIN
      : null;
  const maxHeight =
    heightAboveBottomControls !== null &&
    heightAboveBottomControls >= LAYER_PANEL_MIN_HEIGHT_AVOIDING_BOTTOM_CONTROLS
      ? heightAboveBottomControls
      : mapRect.bottom - panelRect.top - LAYER_PANEL_MAP_MARGIN;
  return {
    maxHeight: Math.floor(maxHeight),
    maxWidth: Math.floor(
      panelRect.right - mapRect.left - LAYER_PANEL_MAP_MARGIN
    ),
  };
};

/** 項目名の横に出す小さな補足バッジ */
const LayerBadge: React.FC<{ text?: string }> = ({ text }) =>
  text ? (
    <span
      style={{
        marginLeft: "4px",
        padding: "0 5px",
        borderRadius: "4px",
        background: "#f3f4f6",
        color: "#6b7280",
        fontSize: "11px",
        lineHeight: "16px",
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </span>
  ) : null;

export const LayerGroupControl: React.FC<LayerGroupControlProps> = ({
  overlayGroups,
  overlayVisibility,
  onToggleOverlay,
  choiceValues = {},
  onSelectChoice,
  lockedGroupTitles,
  layerBadges,
  yearAvailability,
  onYearListToggle,
}) => {
  const lockedTitleSet = useMemo(
    () => new Set(lockedGroupTitles ?? []),
    [lockedGroupTitles]
  );
  // スマホ・タブレット幅ではホバーで閉じられないため、閉じるボタンを表示する
  const isNarrowScreen = useIsMobile();
  const map = useMap();
  const layerPanelRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  // SP でパネルを地図の枠の中に収めるための最大の高さ・幅（PC では使わない）
  const [mobilePanelBounds, setMobilePanelBounds] = useState<{
    maxHeight: number;
    maxWidth: number;
  } | null>(null);
  // ピン留め中はホバーが外れてもレイヤー選択パネルを閉じない（状態はこのコンポーネント内のみで保持）
  const [isPinned, setIsPinned] = useState(false);
  const [layerDescriptionPanel, setLayerDescriptionPanel] =
    useState<LayerDescriptor | null>(null);
  const [layerDescriptionPanelAnchorEl, setLayerDescriptionPanelAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  // フォルダ（type: "folder"）の開閉状態。キーはフォルダの見出し。初期状態は閉じる
  const [expandedFolders, setExpandedFolders] = useState<
    Record<string, boolean>
  >({});
  // yearsLayout: "list" の年度リストの開閉状態。キーはレイヤー名。初期状態は閉じる
  const [expandedYearLists, setExpandedYearLists] = useState<
    Record<string, boolean>
  >({});

  const toggleFolder = useCallback((title: string): void => {
    setExpandedFolders((prev) => ({ ...prev, [title]: !prev[title] }));
  }, []);

  const setYearListExpanded = useCallback(
    (name: string, expanded: boolean): void => {
      setExpandedYearLists((prev) => ({ ...prev, [name]: expanded }));
    },
    []
  );

  // SP ではパネルを開いている間、地図の大きさに合わせてパネルの最大の高さ・幅を測り直す
  useLayoutEffect(() => {
    if (!isNarrowScreen || !isOpen) return;
    const mapContainer = map.getContainer();
    const update = (): void => {
      const panel = layerPanelRef.current;
      if (!panel) return;
      const next = calcMobileLayerPanelBounds(mapContainer, panel);
      // 値が変わらない時は state を更新せず、再描画を起こさない
      setMobilePanelBounds((prev) =>
        prev?.maxHeight === next.maxHeight && prev.maxWidth === next.maxWidth
          ? prev
          : next
      );
    };
    update();
    window.addEventListener("resize", update);
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(mapContainer);
    return () => {
      window.removeEventListener("resize", update);
      observer?.disconnect();
    };
  }, [isNarrowScreen, isOpen, map]);

  const mobileBounds = isNarrowScreen ? mobilePanelBounds : null;

  /**
   * レイヤー説明のヘルプボタンをクリックした時の処理
   */
  const handleHelpClick = useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement>,
      layer: LayerDescriptor
    ): void => {
      event.preventDefault();
      event.stopPropagation();
      setLayerDescriptionPanelAnchorEl(event.currentTarget);
      setLayerDescriptionPanel(layer);
    },
    []
  );

  /**
   * レイヤー説明パネルが閉じられた時の処理
   */
  const handleHelpClose = useCallback(
    (
      event: React.SyntheticEvent,
      _reason: "backdropClick" | "escapeKeyDown"
    ): void => {
      setLayerDescriptionPanelAnchorEl(null);
      setLayerDescriptionPanel(null);

      // ピン留め中はレイヤー選択パネルを閉じない
      if (isPinned) {
        return;
      }

      // レイヤー選択パネル上にマウスポインタがいる場合は、説明パネルのみ閉じる
      // レイヤー選択パネル外にマウスポインタがいる場合は、説明パネルとレイヤー選択パネル両方を閉じる
      // ESCキーで閉じた場合はレイヤー説明パネルのみを閉じます
      const nativeEvent = event.nativeEvent;
      if (
        nativeEvent instanceof MouseEvent &&
        typeof nativeEvent.clientX === "number" &&
        typeof nativeEvent.clientY === "number"
      ) {
        const rect = layerPanelRef.current?.getBoundingClientRect();
        if (rect) {
          // マウス座標がレイヤー選択パネルの矩形内にあるか判定
          const isClickInsidePanel =
            nativeEvent.clientX >= rect.left &&
            nativeEvent.clientX <= rect.right &&
            nativeEvent.clientY >= rect.top &&
            nativeEvent.clientY <= rect.bottom;

          if (!isClickInsidePanel) {
            setIsOpen(false);
          }
        } else {
          // レイヤー選択パネルの座標が取れない場合は説明パネルを閉じる
          setLayerDescriptionPanel(null);
          setIsOpen(false);
        }
      }
    },
    [isPinned]
  );

  /**
   * 年別選択が可能なオーバーレイ項目のUIをレンダリングする関数
   *
   * UIの動作仕様:
   * 1. 親チェックボックス: レイヤー全体の表示/非表示を切り替えます。
   *    - ONにした時: 最初の年度（years[0]）も自動的に有効化します。
   *    - OFFにした時: 親レイヤーおよび全ての年度レイヤーを無効化します。
   * 2. 子ラジオボタン（年度選択）: 親が有効な時のみ操作可能です。
   *    - 選択した年度のみを有効にし、他の年度は無効化します。
   *
   * yearsLayout が "list" の項目はここではなく renderYearListOverlayGroup で描画します。
   *
   * @param overlayVisibility 全レイヤーの可視状態
   * @param layer 対象のレイヤー情報
   * @param disabled 項目全体を無効化するかどうか（ズームレベル制限時など）
   */
  const renderYearSelectableOverlayGroup = useCallback(
    (
      overlayVisibility: Record<string, boolean>,
      layer: YearLayerDescriptor,
      disabled?: boolean
    ): JSX.Element => {
      const { name, label = name, years, maxWidth } = layer;
      if (!years) return <React.Fragment />;
      return (
        <React.Fragment key={name}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              opacity: disabled ? 0.4 : 1,
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                flex: 1,
                minWidth: 0,
                cursor: disabled ? "not-allowed" : "pointer",
              }}
            >
              <input
                type="checkbox"
                disabled={disabled}
                checked={Boolean(overlayVisibility[name])}
                onChange={(event) => {
                  if (event.target.checked) {
                    onToggleOverlay(name, true);
                    onToggleOverlay(`${name}_${years[0]}`, true);
                  } else {
                    onToggleOverlay(name, false);
                    years.forEach((year) => {
                      onToggleOverlay(`${name}_${year}`, false);
                    });
                  }
                }}
              />
              <span>{label}</span>
            </label>
            {layer.description && (
              <IconButton
                size="small"
                onClick={(e) => {
                  handleHelpClick(e, layer);
                }}
                style={{ marginLeft: "auto", padding: "2px", color: "#9ca3af" }}
                aria-label={`${label}の詳細を表示`}
              >
                <HelpOutlineIcon style={{ fontSize: "16px" }} />
              </IconButton>
            )}
          </div>
          <div
            data-years-layout="row"
            style={{
              marginLeft: "24px",
              display: "flex",
              flexDirection: "row", // 横並びに設定
              columnGap: "12px", // 横方向のラジオボタン間の余白
              flexWrap: "wrap", // 幅が足りない場合は折り返しを許可
              opacity: disabled ? 0.4 : 1,
              maxWidth,
            }}
          >
            {years.map((year) => {
              return (
                <label
                  key={year}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  <input
                    disabled={!overlayVisibility[name]}
                    type="radio"
                    name={`year-select-${name}`}
                    checked={overlayVisibility[`${name}_${year}`]}
                    value={year}
                    onChange={(event) => {
                      const selectedYear = Number(event.target.value);
                      // 選択されている年を表示
                      onToggleOverlay(`${name}_${selectedYear}`, true);
                      // 選択されていない年を非表示
                      years
                        .filter((year) => year !== selectedYear)
                        .forEach((year) => {
                          onToggleOverlay(`${name}_${year}`, false);
                        });
                    }}
                  />
                  <span>{year}</span>
                </label>
              );
            })}
          </div>
        </React.Fragment>
      );
    },
    [onToggleOverlay, handleHelpClick]
  );

  /**
   * 年度選択を折りたたみリストで行うオーバーレイ項目（yearsLayout: "list"）のUIをレンダリングする関数
   *
   * 年度が多い項目向けに、フォルダ（type: "folder"）と同じ見出しボタンで開閉する。
   * 見出しにチェックボックスは無く、年度チェックボックスが ON/OFF を兼ねる。
   *
   * UIの動作仕様:
   * 1. 見出しボタン: 年度リストの開閉のみ（初期状態は閉じる）
   * 2. 年度チェックボックス（同時に1つだけ ON）:
   *    - ON にした時: レイヤー全体とその年度を有効化し、他の年度は無効化します。
   *    - OFF にした時: その年度とレイヤー全体を無効化します。
   *
   * @param overlayVisibility 全レイヤーの可視状態
   * @param layer 対象のレイヤー情報
   * @param disabled 項目全体を無効化するかどうか（ズームレベル制限時など）
   */
  const renderYearListOverlayGroup = useCallback(
    (
      overlayVisibility: Record<string, boolean>,
      layer: YearLayerDescriptor,
      disabled?: boolean
    ): JSX.Element => {
      const { name, label = name, years } = layer;
      const isExpanded = Boolean(expandedYearLists[name]);
      const isLayerActive = Boolean(overlayVisibility[name]);
      const availability = yearAvailability?.[name];
      return (
        <div key={name} style={{ opacity: disabled ? 0.4 : 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <DisclosureHeader
              title={label}
              expanded={isExpanded}
              onToggle={() => {
                setYearListExpanded(name, !isExpanded);
              }}
            />
            <LayerBadge text={layerBadges?.[name]} />
            {layer.description && (
              <IconButton
                size="small"
                onClick={(e) => {
                  handleHelpClick(e, layer);
                }}
                style={{ marginLeft: "auto", padding: "2px", color: "#9ca3af" }}
                aria-label={`${label}の詳細を表示`}
              >
                <HelpOutlineIcon style={{ fontSize: "16px" }} />
              </IconButton>
            )}
          </div>
          {isExpanded && (
            <div
              data-years-layout="list"
              style={{
                display: "grid",
                gap: "2px",
                marginLeft: "12px",
                paddingLeft: "6px",
                borderLeft: "1px solid #e5e7eb",
              }}
            >
              <YearListVisibilityReporter
                name={name}
                onChange={onYearListToggle}
              />
              {availability && (
                <div
                  style={{
                    fontSize: "11px",
                    color: "#6b7280",
                    marginBottom: "2px",
                  }}
                >
                  ● は地図中心付近に写真がある年度
                </div>
              )}
              {years.map((year) => {
                const yearKey = `${name}_${year}`;
                // undefined は未確認。true/false が分かっている年度だけ印・薄さを付ける
                const isAvailable = availability?.[year];
                return (
                  <label
                    key={year}
                    data-available={
                      isAvailable === undefined
                        ? undefined
                        : String(isAvailable)
                    }
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      cursor: disabled ? "not-allowed" : "pointer",
                      whiteSpace: "nowrap",
                      opacity: isAvailable === false ? 0.5 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      disabled={disabled}
                      checked={
                        isLayerActive && Boolean(overlayVisibility[yearKey])
                      }
                      onChange={(event) => {
                        if (event.target.checked) {
                          onToggleOverlay(name, true);
                          onToggleOverlay(yearKey, true);
                          years
                            .filter((other) => other !== year)
                            .forEach((other) => {
                              onToggleOverlay(`${name}_${other}`, false);
                            });
                        } else {
                          onToggleOverlay(yearKey, false);
                          onToggleOverlay(name, false);
                        }
                      }}
                    />
                    <span>{`${year}年`}</span>
                    {isAvailable === true && (
                      <span
                        aria-hidden="true"
                        style={{ color: "#10b981", fontSize: "9px" }}
                      >
                        ●
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      );
    },
    [
      onToggleOverlay,
      handleHelpClick,
      expandedYearLists,
      setYearListExpanded,
      layerBadges,
      yearAvailability,
      onYearListToggle,
    ]
  );

  /**
   * 選択肢（ラジオボタン）による表示切り替えが可能なオーバーレイ項目のUIをレンダリングする関数
   *
   * UIの動作仕様:
   * 1. 親チェックボックス: レイヤー全体の表示/非表示を切り替えます。
   *    - OFFのときも選択値（choiceValues）は保持され、再度ONにすると前回選択していた値に戻ります。
   * 2. 子ラジオボタン（モード/選択肢切り替え）: 親が有効な時のみ操作可能です。
   *    - 選択肢の管理は親コンポーネントの state（choiceValues）で行います。
   *
   * @param overlayVisibility 全レイヤーの可視状態
   * @param layer 対象のレイヤー情報
   * @param disabled 項目全体を無効化するかどうか（ズームレベル制限時など）
   */
  const renderChoiceSelectableOverlayGroup = useCallback(
    (
      overlayVisibility: Record<string, boolean>,
      layer: LayerDescriptor,
      disabled?: boolean
    ): JSX.Element => {
      const { name, label = name, choices } = layer;
      if (!choices) return <React.Fragment />;

      return (
        <React.Fragment key={name}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              opacity: disabled ? 0.4 : 1,
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                flex: 1,
                minWidth: 0,
                cursor: disabled ? "not-allowed" : "pointer",
              }}
            >
              <input
                type="checkbox"
                disabled={disabled}
                checked={Boolean(overlayVisibility[name])}
                onChange={(e) => {
                  onToggleOverlay(name, e.target.checked);
                }}
              />
              <span>{label}</span>
            </label>
            {layer.description && (
              <IconButton
                size="small"
                onClick={(e) => {
                  handleHelpClick(e, layer);
                }}
                style={{ marginLeft: "auto", padding: "2px", color: "#9ca3af" }}
                aria-label={`${label}の詳細を表示`}
              >
                <HelpOutlineIcon style={{ fontSize: "16px" }} />
              </IconButton>
            )}
          </div>
          <div
            style={{
              marginLeft: "24px",
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              columnGap: "12px",
              opacity: disabled ? 0.4 : 1,
            }}
          >
            {choices.map((choice) => (
              <label
                key={choice.value}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="radio"
                  name={`choice-select-${name}`}
                  disabled={Boolean(disabled) || !overlayVisibility[name]}
                  checked={
                    Boolean(overlayVisibility[name]) &&
                    choiceValues[name] === choice.value
                  }
                  value={choice.value}
                  onChange={(event) => {
                    onSelectChoice?.(name, event.target.value);
                  }}
                />
                <span>{choice.label}</span>
              </label>
            ))}
          </div>
        </React.Fragment>
      );
    },
    [onToggleOverlay, onSelectChoice, choiceValues, handleHelpClick]
  );

  /**
   * 1 レイヤー分の項目（チェックボックス行）をレンダリングする関数
   *
   * @param layer 対象のレイヤー情報
   * @param isLocked 所属グループがロックされているか（操作不可にする）
   */
  const renderLayerItem = (
    layer: LayerDescriptor,
    isLocked: boolean
  ): JSX.Element => {
    const { type, name, label = name, disabled, action, description } = layer;
    const effectiveDisabled = Boolean(disabled) || isLocked;
    if (type === "year") {
      // typeがyearで、年度選択可能なレイヤーを表している場合、年度選択UIを表示する
      if (layer.yearsLayout === "list") {
        return renderYearListOverlayGroup(
          overlayVisibility,
          layer,
          effectiveDisabled
        );
      }
      return renderYearSelectableOverlayGroup(
        overlayVisibility,
        layer,
        effectiveDisabled
      );
    } else if (layer.type === "choice") {
      return renderChoiceSelectableOverlayGroup(
        overlayVisibility,
        layer,
        effectiveDisabled
      );
    }

    const handleActionClick = (event: MouseEvent<HTMLButtonElement>): void => {
      event.preventDefault();
      event.stopPropagation();
      action?.onClick();
    };

    return (
      <div
        key={name}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          opacity: effectiveDisabled ? 0.4 : 1,
        }}
      >
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            flex: 1,
            minWidth: 0,
            cursor: effectiveDisabled ? "not-allowed" : "pointer",
          }}
        >
          <input
            type="checkbox"
            disabled={effectiveDisabled}
            checked={Boolean(overlayVisibility[name])}
            onChange={(e) => {
              onToggleOverlay(name, e.target.checked);
            }}
          />
          <span>{label}</span>
        </label>
        <LayerBadge text={layerBadges?.[name]} />
        {description && (
          <IconButton
            size="small"
            onClick={(e) => {
              handleHelpClick(e, layer);
            }}
            style={{
              marginLeft: "auto",
              padding: "2px",
              color: "#9ca3af",
            }}
            aria-label={`${label}の詳細を表示`}
          >
            <HelpOutlineIcon style={{ fontSize: "16px" }} />
          </IconButton>
        )}
        {action && (
          <Tooltip title={action.label}>
            <span>
              <IconButton
                size="small"
                color={action.active ? "primary" : "default"}
                disabled={effectiveDisabled || action.disabled}
                aria-label={action.label}
                onClick={handleActionClick}
                sx={{
                  width: 26,
                  height: 26,
                }}
              >
                {action.icon}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </div>
    );
  };

  /**
   * セクション内の項目（レイヤーまたはフォルダ）をレンダリングする関数
   * フォルダは開閉ボタン付きの小見出しを描き、開いている時だけ中の項目を再帰的に描く
   */
  const renderOverlayItem = (
    item: OverlayItem,
    isLocked: boolean
  ): JSX.Element => {
    if (item.type !== "folder") {
      return renderLayerItem(item, isLocked);
    }
    const { title, items } = item;
    const isExpanded = Boolean(expandedFolders[title]);
    return (
      <div key={`folder-${title}`}>
        <DisclosureHeader
          title={title}
          expanded={isExpanded}
          onToggle={() => {
            toggleFolder(title);
          }}
        />
        {isExpanded && (
          <div
            style={{
              display: "grid",
              gap: "4px",
              marginLeft: "12px",
              paddingLeft: "6px",
              borderLeft: "1px solid #e5e7eb",
            }}
          >
            {items.map((child) => renderOverlayItem(child, isLocked))}
          </div>
        )}
      </div>
    );
  };

  /**
   * オーバーレイグループのレンダリング関数
   * グループ見出しとチェックボックス一覧をまとめて描画する
   */
  const renderOverlayGroup = (
    title: string,
    layers: OverlayItem[],
    isFirst: boolean
  ): JSX.Element => {
    const isLocked = lockedTitleSet.has(title);
    return (
      <div
        key={title}
        style={{
          // セクションの境目が分かるよう、2つ目以降は淡い罫線で区切る（見出し行の下線より控えめな色）
          borderTop: isFirst ? undefined : "1px solid #eef0f3",
          paddingTop: isFirst ? 0 : "8px",
          marginTop: "8px",
          opacity: isLocked ? 0.5 : 1,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: "4px" }}>{title}</div>
        <div style={{ display: "grid", gap: "4px" }}>
          {layers.map((item) => renderOverlayItem(item, isLocked))}
        </div>
      </div>
    );
  };

  return (
    <CustomControl position="topright" prepend>
      {/*
        レイヤー選択パネルはマウスホバーでパネル展開を制御
        マウスが外れた際、レイヤー説明パネルが開いていなければ自動で閉じます。
        レイヤー説明パネル表示中にホバーが外れた場合は閉じず、レイヤー説明パネルを閉じたタイミングで、自動で閉じます。
      */}
      <div
        ref={layerPanelRef}
        onMouseEnter={() => {
          setIsOpen(true);
        }}
        onMouseLeave={() => {
          if (!layerDescriptionPanelAnchorEl && !isPinned) {
            setIsOpen(false);
          }
        }}
        onClick={() => {
          setIsOpen(true);
        }}
        style={{
          position: "relative",
        }}
      >
        {/*
          閉じた状態: レイヤーアイコンのみ表示

          NOTE: 開いた時にこの要素をアンマウントしてはいけない。
          タップ操作では click より先に mouseover が発火してパネルが開くため、
          アンマウントするとタップされた要素がDOMから外れた状態で click が届く。
          Leaflet は e.target の親を辿って leaflet-control の
          クリック伝播抑止フラグ（_leaflet_disable_click）を探すので、
          DOMから外れたノードでは判定に失敗し、地図のクリック（ピン表示）が
          誤発火してしまう。そのため表示/非表示はCSSで切り替える。
        */}
        <div
          style={{
            display: isOpen ? "none" : "grid",
            width: "42px",
            height: "42px",
            background: "white",
            borderRadius: "8px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
            placeItems: "center",
            cursor: "pointer",
          }}
        >
          <LayersIcon fontSize="medium" htmlColor="#4b5563" />
        </div>

        {/* 開いた状態: レイヤー選択パネル全体を表示 */}
        {isOpen && (
          <Box
            style={{
              background: "white",
              paddingLeft: `${LAYER_PANEL_PADDING_X}px`,
              paddingRight: `${LAYER_PANEL_PADDING_X}px`,
              paddingBottom: "6px",
              borderRadius: "8px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
              minWidth: `${LAYER_PANEL_MIN_WIDTH}px`,
              maxHeight: "70vh",
              overflowY: "auto",
              fontSize: "14px",
              color: "#1f2d3d",
              // SP は地図の枠の中に収め、はみ出す分はパネルの中でスクロールさせる
              ...(mobileBounds && {
                boxSizing: "border-box",
                minWidth: `${Math.min(
                  LAYER_PANEL_MIN_WIDTH + LAYER_PANEL_PADDING_X * 2,
                  mobileBounds.maxWidth
                )}px`,
                maxWidth: `${mobileBounds.maxWidth}px`,
                maxHeight: `${mobileBounds.maxHeight}px`,
                overscrollBehavior: "contain",
              }),
            }}
            sx={
              isNarrowScreen
                ? {
                    // SP は指で押しやすいよう、行の高さとアイコンボタンの当たり判定を 44px 以上にする
                    "& label, & button[aria-expanded]": {
                      minHeight: MIN_TAP_TARGET,
                    },
                    // アイコンボタンは見た目の大きさを変えず、当たり判定だけ広げる
                    "& .MuiIconButton-root::after": {
                      content: '""',
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      width: MIN_TAP_TARGET,
                      height: MIN_TAP_TARGET,
                      transform: "translate(-50%, -50%)",
                    },
                    // 「?」と設定ボタンが並ぶ行で、当たり判定が重なりすぎないよう間を空ける
                    "& .MuiIconButton-root + span": {
                      marginLeft: "8px",
                    },
                  }
                : undefined
            }
          >
            {/*
              最上段ヘッダー: 見出し「レイヤー」と、閉じるボタン（狭幅のみ）・ピン留めトグル
              本体と区別できるよう薄いグレー背景と下線を付け、負のマージンでパネルの左右端まで広げる
            */}
            <div
              style={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "4px",
                margin: "0 -16px 4px",
                padding: "4px 10px 4px 16px",
                background: "#f3f4f6",
                borderBottom: "1px solid #e5e7eb",
                borderRadius: "8px 8px 0 0",
              }}
            >
              <span style={{ fontWeight: 600, color: "#374151" }}>
                レイヤー
              </span>
              <div style={{ display: "flex", gap: "4px" }}>
                {isNarrowScreen && (
                  <Tooltip title="閉じる">
                    <IconButton
                      size="small"
                      aria-label="レイヤー選択パネルを閉じる"
                      onClick={(event) => {
                        // 親要素のonClick（パネルを開く処理）に伝播させない
                        event.preventDefault();
                        event.stopPropagation();
                        setIsOpen(false);
                        setIsPinned(false);
                      }}
                      // ヘッダーの上端で当たり判定が切れないよう、ボタン自体を 44px にする。
                      // 負のマージンでヘッダーの高さ・アイコンの位置は 26px の時に近づける
                      sx={{
                        width: MIN_TAP_TARGET,
                        height: MIN_TAP_TARGET,
                        my: "-4px",
                        mr: "-9px",
                        color: "#6b7280",
                      }}
                    >
                      <CloseIcon style={{ fontSize: "18px" }} />
                    </IconButton>
                  </Tooltip>
                )}
                {/* 狭幅ではホバーで閉じないため、ピン留めは不要 */}
                {!isNarrowScreen && (
                  <Tooltip title={isPinned ? "固定を解除" : "パネルを固定"}>
                    <IconButton
                      size="small"
                      aria-label={
                        isPinned
                          ? "レイヤー選択パネルの固定を解除"
                          : "レイヤー選択パネルを固定"
                      }
                      aria-pressed={isPinned}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setIsPinned((prev) => !prev);
                      }}
                      sx={{
                        width: 26,
                        height: 26,
                        color: isPinned ? "#2563eb" : "#9ca3af",
                      }}
                    >
                      {isPinned ? (
                        <PushPinIcon style={{ fontSize: "16px" }} />
                      ) : (
                        <PushPinOutlinedIcon style={{ fontSize: "16px" }} />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            </div>

            {/* オーバーレイグループを表示 */}
            {overlayGroups.map(({ title, layers }, index) =>
              renderOverlayGroup(title, layers, index === 0)
            )}
          </Box>
        )}
      </div>

      {/* レイヤー名の右側のヘルプボタンをクリックした時のコンポーネント */}
      <Popover
        open={Boolean(layerDescriptionPanelAnchorEl)}
        anchorEl={layerDescriptionPanelAnchorEl}
        onClose={handleHelpClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            style: {
              padding: "16px",
              // 狭い画面では左右に 16px ずつ余白を残して画面内に収める（maxWidth は padding を含まない）
              maxWidth: "min(320px, calc(100vw - 64px))",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            },
          },
        }}
      >
        {layerDescriptionPanel && (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Typography
              variant="subtitle2"
              style={{ fontWeight: 600, color: "#1f2d3d" }}
            >
              {layerDescriptionPanel.label ?? layerDescriptionPanel.name}
            </Typography>

            <Typography
              variant="body2"
              style={{ color: "#4b5563", lineHeight: 1.5 }}
            >
              {layerDescriptionPanel.description}
            </Typography>

            {layerDescriptionPanel.locked ? (
              <div
                style={{
                  borderTop: "1px solid #e5e7eb",
                  paddingTop: "12px",
                  marginTop: "8px",
                }}
              >
                <Button
                  component="a"
                  href={layerDescriptionPanel.lockedActionUrl}
                  target={getHrefTarget(
                    layerDescriptionPanel.lockedActionUrl ?? ""
                  )}
                  rel={getHrefRel(layerDescriptionPanel.lockedActionUrl ?? "")}
                  variant="contained"
                  color="primary"
                  size="small"
                  disableElevation
                  endIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2,
                    px: 2,
                  }}
                >
                  {layerDescriptionPanel.lockedActionText ??
                    "ご利用のお問い合わせ"}
                </Button>
              </div>
            ) : (
              <div
                style={{
                  borderTop: "1px solid #e5e7eb",
                  paddingTop: "8px",
                  marginTop: "4px",
                }}
              >
                <Typography
                  variant="caption"
                  style={{ display: "block", color: "#6b7280" }}
                >
                  表示ズームレベル:{" "}
                  <span style={{ fontWeight: 500, color: "#374151" }}>
                    {getZoomLevelDescription(layerDescriptionPanel)}
                  </span>
                </Typography>

                {layerDescriptionPanel.attributionText && (
                  <Typography
                    variant="caption"
                    style={{
                      display: "block",
                      color: "#6b7280",
                      marginTop: "4px",
                    }}
                  >
                    出典: {renderAttributionText(layerDescriptionPanel)}
                  </Typography>
                )}
              </div>
            )}
          </div>
        )}
      </Popover>
    </CustomControl>
  );
};
